import { contactName } from "@/lib/contacts";
import type { AccidentRecord } from "@/lib/types";

/**
 * Callbacks for the others in the accident, booked from one outcome window or one Book a callback
 * (ACL-671, ADR-226 in the platform repo). Pure; `features/also-book-for.tsx` draws it.
 */

/** One callback to book beside the caller's: whose, and the time as core-api takes it. */
export interface AlsoBooking {
  leadId: string;
  name: string;
  /** RFC 3339 with its offset (ADR-080). */
  iso: string;
}

/** Who can be booked beside the caller: the others in the accident with a number of their own that
 *  the reader may open. Those on the caller's number share the caller's call (the dialer has no
 *  one-call-per-number rule, so a second row would ring the same phone twice): they are said, not booked. */
export function alsoBookable(
  parties: readonly AccidentRecord[],
  callerPhone: string | null | undefined,
): { bookable: AccidentRecord[]; shared: AccidentRecord[] } {
  const open = parties.filter((p) => p.may_open && !p.deleted);
  return {
    bookable: open.filter((p) => p.phone && p.phone !== callerPhone),
    shared: open.filter((p) => p.phone && p.phone === callerPhone),
  };
}

/** The fold's one line while it is shut: "Paul and Mia, the others in this accident · not booked". */
export function alsoBookLine(parties: readonly AccidentRecord[], booked: number): string {
  const firsts = parties.map((p) => (contactName(p) ?? "someone").split(" ")[0]);
  const who =
    firsts.length <= 2
      ? firsts.join(" and ")
      : `${firsts.slice(0, -1).join(", ")} and ${firsts[firsts.length - 1]}`;
  const which = parties.length === 1 ? "the other person in this accident" : "the others in this accident";
  return `${who}, ${which} · ${booked === 0 ? "not booked" : `${booked} booked`}`;
}

/** What the toast says once the caller's callback is booked and the others were booked after it. */
export function alsoBookedMessage(
  results: readonly { name: string; when: string | null; problem: string | null }[],
): { title: string; detail: string; tone: "success" | "warning" } | null {
  if (results.length === 0) return null;
  const went = results.filter((r) => r.problem === null);
  const failed = results.filter((r) => r.problem !== null);
  const parts = went.map((r) => `${r.name}${r.when ? ` at ${r.when}` : ""}`);
  const detail = [
    parts.length > 0 ? `Also booked: ${parts.join("; ")}.` : null,
    ...failed.map((r) => `${r.name}: ${r.problem}`),
  ]
    .filter(Boolean)
    .join(" ");
  if (failed.length === 0) return { title: `${went.length + 1} callbacks booked`, detail, tone: "success" };
  if (went.length === 0) return { title: "The others were not booked", detail, tone: "warning" };
  return { title: `${went.length + 1} callbacks booked, ${failed.length} not`, detail, tone: "warning" };
}
